import { useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { ErrorState } from '@/components/feature/AsyncState';
import Modal from '@/components/base/Modal';
import { usePurchases } from '@/hooks/usePurchases';
import PurchaseModal from './components/PurchaseModal';
import PurchaseDetailModal from './components/PurchaseDetailModal';
import PurchaseEditModal from './components/PurchaseEditModal';
import PurchaseStatusBadge from './components/PurchaseStatusBadge';
import AccountsPayable from './components/AccountsPayable';
import { deletePurchase, type Purchase } from '@/services/purchases';

type Tab = 'list' | 'payable';

function formatDate(value: string): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function PurchasesPage() {
  const { data, loading, error, refetch } = usePurchases();
  const [tab, setTab] = useState<Tab>('list');
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [detailFor, setDetailFor] = useState<Purchase | null>(null);
  const [editingFor, setEditingFor] = useState<Purchase | null>(null);
  const [deleting, setDeleting] = useState<Purchase | null>(null);
  const [deletingNow, setDeletingNow] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const totalInvested = data.reduce((sum, p) => sum + p.totalCost, 0);
  const totalPayable = data
    .filter((p) => p.status !== 'cancelled')
    .reduce((sum, p) => sum + p.balance, 0);

  const filtered = data.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      p.invoiceNumber.toLowerCase().includes(q) ||
      p.supplierName.toLowerCase().includes(q) ||
      p.notes.toLowerCase().includes(q)
    );
  });

  async function handleDelete() {
    if (!deleting) return;
    setDeletingNow(true);
    setDeleteError('');
    const { error } = await deletePurchase(deleting.id);
    setDeletingNow(false);
    if (error) {
      setDeleteError(error);
      return;
    }
    setDeleting(null);
    refetch();
  }

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        {error && <ErrorState message={error} onRetry={refetch} />}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Compras</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>{data.length} compras registradas</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                Supabase
              </span>
            </p>
          </div>
          <button
            onClick={() => setShowNew(true)}
            className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
          >
            <i className="ri-add-line mr-1.5"></i>Nueva Compra
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Compras totales</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">{data.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Total invertido</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">${totalInvested.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Cuentas por pagar</p>
            <p className="text-xl font-bold text-red-600 mt-1">${totalPayable.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Ítems adquiridos</p>
            <p className="text-xl font-bold text-accent-600 mt-1">
              {data.reduce((sum, p) => sum + p.itemCount, 0)}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="inline-flex items-center gap-1 bg-background-100 rounded-full p-1">
            <button
              onClick={() => setTab('list')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                tab === 'list' ? 'bg-white text-foreground-950' : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              <i className="ri-shopping-cart-2-line mr-1.5"></i>Compras
            </button>
            <button
              onClick={() => setTab('payable')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                tab === 'payable' ? 'bg-white text-foreground-950' : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              <i className="ri-wallet-3-line mr-1.5"></i>Cuentas por pagar
            </button>
          </div>

          {tab === 'list' && (
            <div className="relative w-full sm:w-auto sm:max-w-sm">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                placeholder="Buscar por factura, proveedor o nota..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full sm:w-72 pl-9 pr-4 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300"
              />
            </div>
          )}
        </div>

        {tab === 'payable' ? (
          <AccountsPayable purchases={data} loading={loading} onOpen={(p) => setDetailFor(p)} />
        ) : (
          <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-100 text-left bg-background-50">
                    <th className="px-5 py-3 text-xs font-medium text-foreground-500">Factura</th>
                    <th className="px-5 py-3 text-xs font-medium text-foreground-500">Proveedor</th>
                    <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Total</th>
                    <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Abonado</th>
                    <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Saldo</th>
                    <th className="px-5 py-3 text-xs font-medium text-foreground-500">Estado</th>
                    <th className="px-5 py-3 text-xs font-medium text-foreground-500">Fecha</th>
                    <th className="px-5 py-3 text-xs font-medium text-foreground-500 w-28"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="px-5 py-10 text-center text-foreground-400">
                        <i className="ri-loader-4-line animate-spin mr-2"></i>Cargando compras...
                      </td>
                    </tr>
                  ) : error ? null : (
                    filtered.map((p) => (
                      <tr
                        key={p.id}
                        className={`border-b border-background-100 hover:bg-background-50 transition-colors ${
                          p.status === 'cancelled' ? 'opacity-60' : ''
                        }`}
                      >
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                              <i className="ri-shopping-cart-2-line text-accent-600"></i>
                            </div>
                            <div className="min-w-0">
                              <p className="text-foreground-900 font-medium whitespace-nowrap">
                                {p.invoiceNumber || 'Sin factura'}
                              </p>
                              <p className="text-[11px] text-foreground-400">{p.itemCount} ítems</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-foreground-700">{p.supplierName || '—'}</td>
                        <td className="px-5 py-3 text-right font-semibold text-foreground-950 whitespace-nowrap">
                          ${p.totalCost.toLocaleString()}
                        </td>
                        <td className="px-5 py-3 text-right text-emerald-600 whitespace-nowrap">
                          ${p.paidAmount.toLocaleString()}
                        </td>
                        <td className="px-5 py-3 text-right whitespace-nowrap">
                          <span
                            className={
                              p.status === 'cancelled'
                                ? 'text-foreground-400'
                                : p.balance > 0
                                  ? 'font-semibold text-red-600'
                                  : 'text-foreground-500'
                            }
                          >
                            ${p.balance.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <PurchaseStatusBadge status={p.status} />
                        </td>
                        <td className="px-5 py-3 text-xs text-foreground-500 whitespace-nowrap">
                          {formatDate(p.createdAt)}
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => setDetailFor(p)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                              title="Ver detalle y abonos"
                            >
                              <i className="ri-eye-line text-foreground-500"></i>
                            </button>
                            <button
                              onClick={() => setEditingFor(p)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                              title="Editar factura, proveedor o notas"
                            >
                              <i className="ri-edit-line text-foreground-500"></i>
                            </button>
                            <button
                              onClick={() => {
                                setDeleting(p);
                                setDeleteError('');
                              }}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors cursor-pointer"
                              title="Eliminar"
                            >
                              <i className="ri-delete-bin-line text-red-500"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {!loading && !error && filtered.length === 0 && (
              <div className="text-center py-12 text-foreground-500">
                <i className="ri-shopping-cart-2-line text-3xl block mb-2"></i>
                <p className="text-sm">
                  {data.length === 0
                    ? 'Aún no hay compras registradas.'
                    : 'No se encontraron compras con ese filtro.'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {showNew && <PurchaseModal onClose={() => setShowNew(false)} onSaved={refetch} />}
      {editingFor && (
        <PurchaseEditModal
          purchase={editingFor}
          onClose={() => setEditingFor(null)}
          onSaved={refetch}
        />
      )}
      {detailFor && (
        <PurchaseDetailModal
          purchaseId={detailFor.id}
          onClose={() => {
            setDetailFor(null);
            refetch();
          }}
        />
      )}
      {deleting && (
        <Modal
          title="Eliminar compra"
          subtitle={`Factura ${deleting.invoiceNumber || 'Sin factura'} — ${deleting.supplierName || 'Proveedor desconocido'}`}
          onClose={() => {
            setDeleting(null);
            setDeleteError('');
          }}
          footer={
            <>
              <button
                type="button"
                onClick={() => {
                  setDeleting(null);
                  setDeleteError('');
                }}
                className="px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 rounded-md transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deletingNow}
                className="px-4 py-2 text-sm font-semibold bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {deletingNow ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-1.5"></i>Eliminando...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line mr-1.5"></i>Eliminar
                  </>
                )}
              </button>
            </>
          }
        >
          <div className="space-y-3">
            {deleteError && (
              <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
                <i className="ri-error-warning-line mt-0.5"></i>
                <span>{deleteError}</span>
              </div>
            )}
            <p className="text-sm text-foreground-600">
              Esta acción no se puede deshacer. Se eliminará la compra y se revierte el stock que
              había sumado al inventario.
            </p>
            <p className="text-xs text-foreground-500">
              Si solo quieres anularla dejando el historial, usa <strong>Cancelar compra</strong> en el detalle.
            </p>
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
}